import axios from 'axios';

// Free APIs - No API key required
const REST_COUNTRIES_API = 'https://restcountries.com/v3.1';
const IP_GEOLOCATION_API = 'https://ipapi.co/json/';

/**
 * Fetch all currencies from REST Countries API
 * Returns comprehensive list including African currencies (KES, UGX, etc.)
 */
export const fetchAllCurrencies = async () => {
  try {
    const response = await axios?.get(`${REST_COUNTRIES_API}/all?fields=name,currencies,cca2`);
    const countries = response?.data || [];

    // Extract unique currencies with country info
    const currencyMap = new Map();

    countries?.forEach(country => {
      const currencies = country?.currencies || {};
      Object.entries(currencies)?.forEach(([code, details]) => {
        if (code && details?.name) {
          if (!currencyMap?.has(code)) {
            currencyMap?.set(code, {
              code,
              name: details?.name,
              symbol: details?.symbol || '',
              countries: [country?.name?.common]
            });
          } else {
            // Add country to existing currency
            const existing = currencyMap?.get(code);
            existing?.countries?.push(country?.name?.common);
          }
        }
      });
    });

    // Convert to array and sort alphabetically
    const currencyList = Array.from(currencyMap?.values())?.sort((a, b) => a?.code?.localeCompare(b?.code));

    return currencyList;
  } catch (error) {
    console.error('Error fetching currencies:', error);
    // Return fallback list with African currencies
    return getFallbackCurrencies();
  }
};

/**
 * Detect user's location and return their country's currency
 */
export const detectUserCurrency = async () => {
  try {
    const response = await axios?.get(IP_GEOLOCATION_API, { timeout: 5000 });
    const countryCode = response?.data?.country_code;

    if (!countryCode) {
      return 'USD'; // Default fallback
    }

    // Fetch country details to get currency
    const countryResponse = await axios?.get(
      `${REST_COUNTRIES_API}/alpha/${countryCode}?fields=currencies`
    );

    const currencies = countryResponse?.data?.currencies || {};
    const currencyCode = Object.keys(currencies)?.[0];

    return currencyCode || 'USD';
  } catch (error) {
    console.error('Error detecting user currency:', error);
    return 'USD'; // Default fallback
  }
};

/**
 * Get currency details by code
 */
export const getCurrencyByCode = async (code) => {
  try {
    const response = await axios?.get(
      `${REST_COUNTRIES_API}/currency/${code}?fields=name,currencies`
    );
    const country = response?.data?.[0];
    const currencies = country?.currencies || {};
    return currencies?.[code] || null;
  } catch (error) {
    console.error('Error fetching currency details:', error);
    return null;
  }
};

/**
 * Format currency options for Select component
 */
export const formatCurrencyOptions = (currencies) => {
  return currencies?.map(currency => ({
    value: currency?.code,
    label: `${currency?.code} - ${currency?.name}${currency?.symbol ? ` (${currency?.symbol})` : ''}`
  }));
};

/**
 * Fallback currency list with African currencies included
 */
const getFallbackCurrencies = () => {
  return [
    { code: 'AED', name: 'United Arab Emirates Dirham', symbol: 'د.إ', countries: ['United Arab Emirates'] },
    { code: 'AUD', name: 'Australian Dollar', symbol: '$', countries: ['Australia'] },
    { code: 'BWP', name: 'Botswana Pula', symbol: 'P', countries: ['Botswana'] },
    { code: 'CAD', name: 'Canadian Dollar', symbol: '$', countries: ['Canada'] },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', countries: ['Switzerland'] },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', countries: ['China'] },
    { code: 'EGP', name: 'Egyptian Pound', symbol: '£', countries: ['Egypt'] },
    { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', countries: ['Ethiopia'] },
    { code: 'EUR', name: 'Euro', symbol: '€', countries: ['European Union'] },
    { code: 'GBP', name: 'British Pound Sterling', symbol: '£', countries: ['United Kingdom'] },
    { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', countries: ['Ghana'] },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', countries: ['India'] },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', countries: ['Japan'] },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', countries: ['Kenya'] },
    { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', countries: ['Morocco'] },
    { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', countries: ['Mauritius'] },
    { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', countries: ['Malawi'] },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', countries: ['Mexico'] },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', countries: ['Nigeria'] },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: '$', countries: ['New Zealand'] },
    { code: 'RWF', name: 'Rwandan Franc', symbol: 'Fr', countries: ['Rwanda'] },
    { code: 'SGD', name: 'Singapore Dollar', symbol: '$', countries: ['Singapore'] },
    { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', countries: ['Tanzania'] },
    { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', countries: ['Uganda'] },
    { code: 'USD', name: 'United States Dollar', symbol: '$', countries: ['United States'] },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', countries: ['South Africa'] },
    { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', countries: ['Zambia'] },
    { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: '$', countries: ['Zimbabwe'] }
  ];
};

/**
 * Search currencies by name or code
 */
export const searchCurrencies = (currencies, searchTerm) => {
  if (!searchTerm) return currencies;
  
  const term = searchTerm?.toLowerCase();
  return currencies?.filter(currency => 
    currency?.code?.toLowerCase()?.includes(term) ||
    currency?.name?.toLowerCase()?.includes(term) ||
    currency?.countries?.some(country => country?.toLowerCase()?.includes(term))
  );
};
